import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, ReplaySubject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { createGuid } from '../../../core/helpers/utility';
import { ICart, IProductMain } from '../../../core/models/products.models';
import { AppService } from '../../../store/app.service';
import { ShopApiService } from '../shop.apiservice';
import { UtilityService } from '../../../core/helpers/utility.service';
import { IUser } from '../../../core/models/auth.models';
import { ClientsApiService } from '../../clients/clients.apiservice';

@Component({
  selector: 'app-products-list',
  templateUrl: './products-list.component.html',
  styleUrls: ['./products-list.component.scss']
})

export class ProductsListComponent implements OnInit, OnDestroy {
  pageNumber = 1;
  pageSize = 12;

  tierId: number | null = null;
  pricingTiers: Record<number, string> = {
    1: 'Basic Tier',
    2: 'Standard Tier',
    3: 'Premium Tier',
    4: 'Elite Tier',
  };

  allProducts: Array<IProductMain> = new Array<IProductMain>();
  holdProducts: Array<IProductMain> = new Array<IProductMain>();
  tempProducts: Array<IProductMain> = new Array<IProductMain>();

  allMyCompanies: {id: number; name: string}[] = [];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  public userRoles: Array<number> = [];
  activeUser: IUser = {} as IUser;
  
  doneLoading: boolean = false;

  constructor(
    private shopApiService: ShopApiService,
    private appService: AppService,
    private route: ActivatedRoute,
    private router: Router,
    public uService: UtilityService,
    private clientService: ClientsApiService,
  ) {
    this.userRoles = [...this.uService.getUserRoles()];
    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(u => {
        this.activeUser = u;
      });
    if (this.activeUser.roles[0].code === 8) {
      // Employee - Shouldn't see any products
      this.router.navigate(['/pages/dashboard']);
    }
  }

  ngOnInit(): void {
    this.appService.setPageTitle$('SHOP');

    this.route.queryParams.subscribe(params => {
      if (params['t'] !== undefined) {
        this.tierId = Number(params['t']);
      }
      // 0 = NonTiered, 1 = Basic, 2 = Standard, 3 = Premium, 4 = Elite
    });

    if (this.activeUser.roles[0].code === 9) {
      this.appService.getAllCampaigns$()
        .pipe(skip(1), takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          const clientIds: string[] = Array.from(new Set<string>((response?.data || []).map((campaign: any) => campaign.clientId).filter(Boolean)));

          if (!clientIds.length) {
            this.allProducts = [];
            this.finalizeProductLoading();
            return;
          }

          forkJoin(clientIds.map((clientId: string) => this.clientService.getClientDetails(clientId).pipe(takeUntil(this.destroyed$))))
            .pipe(takeUntil(this.destroyed$))
            .subscribe((clientResponses: any[]) => {
              const clientBrands: string[] = Array.from(new Set<string>(clientResponses.map((clientResponse: any) => clientResponse?.data?.brand).filter(Boolean)));

              this.shopApiService.getProducts()
                .pipe(takeUntil(this.destroyed$))
                .subscribe((productsResponse: any) => {
                  const visibleProducts = (productsResponse.data || []).filter((product: IProductMain) =>
                    !product.isBundleOnly && clientBrands.some((brand: string) => product.companyNames?.indexOf(brand) > -1)
                  );

                  this.allProducts = Array.from(new Map<string, IProductMain>(visibleProducts.map((product: IProductMain) => [product.id, product] as [string, IProductMain])).values());
                  if (this.tierId !== null) {
                    this.getTieredProducts().then(tieredProducts => {
                      this.allProducts = tieredProducts;
                      this.finalizeProductLoading();
                    });
                  } else {
                    this.finalizeProductLoading();
                  }
                });
            });
        });
      
      this.appService.fetchAllCampaigns({});
    } else {
      this.shopApiService.getAllProducts()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          this.allProducts = [...response.data];
          if (this.tierId !== null) {
            this.getTieredProducts().then(tieredProducts => {
              this.allProducts = tieredProducts;
              this.finalizeProductLoading();
            });
          } else {
            this.finalizeProductLoading();
          }
        });
    }
  }

  finalizeProductLoading(): void {
    this.allProducts.sort((a, b) => a.display!.localeCompare(b.display!));
    this.tempProducts = this.allProducts.slice(0, this.pageSize);
    this.holdProducts = this.allProducts;
    this.allProducts.forEach((p: IProductMain) => {
      if (p.companyNames) { // Add null check for companyNames
        p.companyNames.split(',').forEach((pc: string) => {
          if (!this.allMyCompanies.find((c) => c.name === pc) && pc) {
            this.allMyCompanies.push({ id: this.allMyCompanies.length, name: pc! });
          }
        });
      }
    });
    this.allMyCompanies.sort((a, b) => a.name.localeCompare(b.name));
    this.doneLoading = true;
  }

  async getTieredProducts(): Promise<IProductMain[]> {
    const tieredProducts: IProductMain[] = [];
    const tierNames = Object.values(this.pricingTiers);

    if (this.tierId !== null && this.tierId !== 0) {
      const tierName = this.pricingTiers[this.tierId]; // Access tierName only if tierId is not null
      const productPromises = this.allProducts.map((p: IProductMain) =>
        this.shopApiService.getProductDetails(p.id)
          .pipe(takeUntil(this.destroyed$))
          .toPromise()
          .then((res: any) => {
            const productDetails = res.data;
            if (productDetails?.name?.includes(tierName)) {
              tieredProducts.push(productDetails);
            }
          })
      );

      await Promise.all(productPromises); // Wait for all product details to be fetched
      return tieredProducts;
    } else if (this.tierId === 0) {
      const productPromises = this.allProducts.map((p: IProductMain) =>
        this.shopApiService.getProductDetails(p.id)
          .pipe(takeUntil(this.destroyed$))
          .toPromise()
          .then((res: any) => {
            const productDetails = res.data;
            // Check if the product name does not include any tier names
            if (!tierNames.some(tierName => productDetails?.name?.includes(tierName))) {
              tieredProducts.push(productDetails);
            }
          })
      );

      await Promise.all(productPromises); // Wait for all product details to be fetched
      return tieredProducts;
    }

    return []; // Return an empty array if tierId is null
  }

  onCompanyChange(event: any) {
    if (event !== undefined) {
      this.allProducts = this.holdProducts.filter((p: IProductMain) => p.companyNames?.indexOf(event.name) > -1);
    } else {
      this.allProducts = this.holdProducts;
    }
    this.onPageChange(1);
  }

  onAddtoCart(id: string) {
    if (this.userRoles.includes(1) || this.userRoles.includes(4) || this.userRoles.includes(7) || this.userRoles.includes(9)) {
      this.shopApiService.saveCart({id: createGuid(), productId: id, currentStep: 1} as ICart)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((r: any) => {
          if (r.good) {
            this.router.navigate(['/pages/shop/cart', r.data.id]);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onPageChange(pageNumber: number) {
    this.pageNumber = pageNumber;
    this.tempProducts = this.allProducts.slice((this.pageNumber - 1) * this.pageSize, this.pageNumber * this.pageSize);
  }
}
